import {
  AsYouType,
  getCountryCallingCode,
  getExampleNumber,
  isValidPhoneNumber,
  Metadata,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';

export type { CountryCode };

export const DEFAULT_COUNTRY: CountryCode = 'GH';

export const getPhoneExampleForCountry = (country: CountryCode): string => {
  try {
    return getExampleNumber(country, examples)?.formatNational() ?? '';
  } catch {
    return '';
  }
};

export const maxNationalDigitsForCountry = (country: CountryCode): number => {
  try {
    const metadata = new Metadata();
    metadata.selectNumberingPlan(country);
    const lengths = metadata.numberingPlan?.possibleLengths() ?? [];
    if (lengths.length > 0) {
      return Math.max(...lengths) + 1;
    }
  } catch {
    // fall through
  }
  return 15 - String(getCountryCallingCode(country)).length + 1;
};

export const normalizePhoneForCountry = (
  phone: string,
  country: CountryCode,
): string | null => {
  const raw = phone.trim();
  if (!raw) return null;

  try {
    const parsed = parsePhoneNumberFromString(raw, country);
    if (parsed?.isValid()) return parsed.number;
    if (isValidPhoneNumber(raw, country)) {
      return parsePhoneNumberFromString(raw, country)?.number ?? null;
    }
  } catch {
    // fall through
  }
  return null;
};

export const isValidPhoneForCountry = (
  phone: string,
  country: CountryCode,
): boolean => normalizePhoneForCountry(phone, country) != null;

export const formatNationalPhoneInput = (
  raw: string,
  country: CountryCode,
): string => {
  let digits = raw.replace(/\D/g, '');
  if (!digits) return '';

  const dial = String(getCountryCallingCode(country));
  if (digits.startsWith(dial) && digits.length > dial.length + 4) {
    digits = digits.slice(dial.length).replace(/^0+/, '');
  }

  const max = maxNationalDigitsForCountry(country);
  if (digits.length > max) digits = digits.slice(0, max);

  return new AsYouType(country).input(digits) || digits;
};

export const nationalDisplayFromE164 = (e164: string): string => {
  const parsed = parsePhoneNumberFromString(e164);
  return parsed?.formatNational() ?? e164;
};

export const dialCode = (country: CountryCode): string => `+${getCountryCallingCode(country)}`;

export const flagEmoji = (country: CountryCode): string =>
  country
    .toUpperCase()
    .split('')
    .map((letter) => String.fromCodePoint(0x1f1e6 + letter.charCodeAt(0) - 65))
    .join('');

export const countryName = (country: CountryCode, locale: string): string => {
  try {
    const names = new Intl.DisplayNames([locale], { type: 'region' });
    return names.of(country) ?? country;
  } catch {
    return country;
  }
};
